import { useCallback, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { useChatStore, useNotificationStore, useRoomStore } from '@src/stores';
import { EMeetingType, EMessageType } from '@src/types';

const useChatDropDown = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');

  const { participantsPeers, isPrivateChatEnabled } = useRoomContext();

  const { selectedReceiverData, setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({
      selectedReceiverData: state.selectedReceiverData,
      setSelectedReceiverData: state.setSelectedReceiverData,
    }))
  );

  const { messagesNotifications } = useNotificationStore(
    useShallow((state) => ({
      messagesNotifications: state.messagesNotifications,
    }))
  );

  const { meetingType } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
    }))
  );

  const chatList = useMemo(() => {
    const list: { id: string; type: EMessageType; name: string }[] = [
      { id: EMessageType.PUBLIC, type: EMessageType.PUBLIC, name: 'Public chat' },
    ];
    //make it apprea only for panelsit
    meetingType === EMeetingType.WEBINAR &&
      list.push({ id: EMessageType.PANELIST, type: EMessageType.PANELIST, name: 'Panelist Group Chat' });
    if (!isPrivateChatEnabled) return list;
    const unreadPeers = participantsPeers
      .filter((peer) => {
        const messages = messagesNotifications.get(peer.peer_info.peer_id);
        return messages && messages.length > 0;
      })
      .sort((a, b) => {
        const messagesA = messagesNotifications.get(a.peer_info.peer_id);
        const messagesB = messagesNotifications.get(b.peer_info.peer_id);
        const latestA = messagesA?.[messagesA.length - 1]?.timestamp ?? '';
        const latestB = messagesB?.[messagesB.length - 1]?.timestamp ?? '';

        return new Date(latestB).getTime() - new Date(latestA).getTime();
      });

    const readPeers = participantsPeers.filter((peer) => {
      return !unreadPeers.some((unreadPeer) => unreadPeer.peer_info.peer_id === peer.peer_info.peer_id);
    });

    const sortedParticipants = [...unreadPeers, ...readPeers];

    sortedParticipants.forEach((peer) => {
      if (!peer.isMe) {
        const {
          peer_info: { peer_id, peer_name },
        } = peer;
        list.push({ id: peer_id, type: EMessageType.PRIVATE, name: peer_name });
      }
    });
    return list;
  }, [meetingType, isPrivateChatEnabled, participantsPeers, messagesNotifications]);

  const selectedChatName = useMemo(
    () => chatList.find((chat) => chat.id === selectedReceiverData.value)?.name || 'Public chat',
    [chatList, selectedReceiverData]
  );

  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setSearch('');
    },
    [setAnchorEl, setSearch]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectChat = useCallback(
    ({ id, type }: { id: string; type: EMessageType }) => {
      setSelectedReceiverData({ key: type, value: id });
      setSearch('');
      handleClose();
    },
    [setSelectedReceiverData]
  );

  const filteredChats = useMemo(() => {
    return search ? chatList.filter((chat) => chat?.name.toLowerCase().includes(search.toLowerCase())) : chatList;
  }, [search, chatList]);

  return {
    filteredChats,
    anchorEl,
    search,
    setSearch,
    selectedReceiverData,
    setSelectedReceiverData,
    participantsPeers,
    messagesNotifications,
    selectedChatName,
    open,
    handleClick,
    handleSelectChat,
    chatList,
    handleClose,
  };
};

export default useChatDropDown;
